import axiosInstance from "./axios";

/**
 * Client del widget di supporto (Jinkō) verso il servizio support-agent/.
 * Gemello di app/src/lib/supportClient.js, adattato al CRA del pannello.
 *
 * Passa da axiosInstance DI PROPOSITO, non da un fetch proprio: eredita
 * l'Authorization sempre fresco e il giro 401 → refresh → retry
 * dell'interceptor. Col token a 60 minuti una chat lunga SUPERA la scadenza:
 * senza quel giro il widget si romperebbe a metà conversazione. Non
 * aggiungere qui gestione token a mano.
 *
 * Content-Type SEMPRE esplicito: l'interceptor di richiesta, quando manca,
 * forza application/x-www-form-urlencoded — e il servizio parla JSON.
 *
 * URL assoluta: axios ignora la baseURL (che punta al backend Lumen) quando
 * l'url inizia con http — il servizio del bot è un host diverso.
 */
const SUPPORT_URL = (process.env.REACT_APP_SUPPORT_URL || "").replace(/\/$/, "");

export function isSupportConfigured() {
  return Boolean(SUPPORT_URL);
}

/**
 * Il servizio dice in /healthz se la dettatura vocale è configurata: il
 * widget mostra il microfono solo se c'è (e se il browser sa registrare).
 */
export async function fetchVoiceCapability() {
  try {
    const { data } = await axiosInstance.get(`${SUPPORT_URL}/healthz`);
    return Boolean(data?.voice);
  } catch {
    return false;
  }
}

/**
 * Blob audio → testo. Il blob viaggia GREZZO nel body (niente multipart); il
 * mime del recorder è il content-type (iOS registra audio/mp4, Chrome webm).
 */
export async function transcribeAudio(blob, lang) {
  const { data } = await axiosInstance.post(
    `${SUPPORT_URL}/api/transcribe?lang=${lang === "en" ? "en" : "it"}`,
    blob,
    { headers: { "Content-Type": blob.type || "audio/webm" }, timeout: 60000 }
  );
  return String(data?.text || "").trim();
}

/**
 * Cronologia visibile della PROPRIA conversazione (ponte tra le aree: la
 * cronologia non attraversa i domini, la serve il server — finché la
 * conversazione vive, TTL 30'). [] se scaduta, altrui o irraggiungibile.
 * @returns {Promise<Array<{role, text, actions?, ticketId?}>>}
 */
export async function fetchConversationTranscript(conversationId) {
  if (!conversationId) return [];
  try {
    const { data } = await axiosInstance.get(
      `${SUPPORT_URL}/api/conversation?id=${encodeURIComponent(conversationId)}`
    );
    return Array.isArray(data?.messages) ? data.messages : [];
  } catch {
    return [];
  }
}

/**
 * Manda un messaggio a Jinkō.
 * @returns {Promise<{conversation_id, reply, actions, escalated, blocked}>}
 * Lancia in caso di errore rete/5xx/401 persistente (il chiamante mostra la
 * bolla "non raggiungibile"); il primo 401 lo gestisce l'interceptor col
 * refresh, e per gli host esterni un 401 persistente NON slogga il pannello.
 */
/**
 * Lista "le mie conversazioni" (stadio 3-B, store DB lato servizio).
 * { store: false, conversations: [] } quando lo store non e' attivo o non
 * raggiungibile: il widget nasconde la voce di cronologia, niente errori.
 */
export async function fetchConversationsList() {
  try {
    const { data } = await axiosInstance.get(`${SUPPORT_URL}/api/conversations`);
    return {
      store: Boolean(data?.store),
      conversations: Array.isArray(data?.conversations) ? data.conversations : [],
    };
  } catch {
    return { store: false, conversations: [] };
  }
}

/**
 * Timbra come VISTI i fili indicati. Sostituisce il timbro in sessionStorage
 * (round 17b): quello viveva nella scheda del browser, quindi non attraversava
 * app <-> pannello ne' i dispositivi, e il pallino si riaccendeva su risposte
 * gia' lette. Fire-and-forget: se fallisce, il pallino resta acceso fino al
 * giro dopo — mai un errore a schermo per un dettaglio di stato.
 */
export async function markConversationsSeen(uuids) {
  if (!Array.isArray(uuids) || !uuids.length) return;
  try {
    await axiosInstance.post(`${SUPPORT_URL}/api/conversations/seen`, { uuids });
  } catch {}
}

export async function sendSupportMessage({ message, conversationId, lang, route }) {
  const { data } = await axiosInstance.post(
    `${SUPPORT_URL}/api/chat`,
    {
      message,
      conversation_id: conversationId || undefined,
      // `surface` = da quale ambiente dell'ecosistema arriva la domanda: finisce
      // nella notifica al team, che così sa dove sta l'utente prima di leggere.
      context: { lang, route, surface: "network" },
    },
    // Un turno col modello può superare il minuto coi tool; senza timeout un
    // servizio appeso terrebbe lo spinner all'infinito.
    { headers: { "Content-Type": "application/json" }, timeout: 90000 }
  );
  return data;
}

/**
 * Evento di arrivo dell'accompagnamento (3-C): "l'utente è approdato su
 * questa pagina". Nessun testo dell'utente. Il server applica i guardrail
 * (guida attiva, route attesa, tetti) e risponde {skipped:true} quando il
 * turno auto non deve partire: il chiamante in quel caso non mostra nulla.
 */
export async function sendArrivedEvent({ conversationId, lang, route }) {
  const { data } = await axiosInstance.post(
    `${SUPPORT_URL}/api/chat`,
    {
      event: "arrived",
      conversation_id: conversationId,
      context: { lang, route, surface: "network" },
    },
    { headers: { "Content-Type": "application/json" }, timeout: 90000 }
  );
  return data;
}

/**
 * Le 3 domande pronte per la pagina corrente (round 11). Contenuto curato nel
 * brain e servito dal servizio: aggiornarle non richiede un rebuild del
 * widget. Lista vuota se il servizio non risponde -> il chiamante usa le sue.
 */
export async function fetchSuggestions({ route, lang }) {
  try {
    const { data } = await axiosInstance.get(
      `${SUPPORT_URL}/api/suggestions?route=${encodeURIComponent(route || "")}&lang=${lang === "en" ? "en" : "it"}`
    );
    return Array.isArray(data?.suggestions) ? data.suggestions : [];
  } catch {
    return [];
  }
}
